import type { CSSProperties, ReactNode } from "react";
import { Card as AntdCard, type CardProps as AntdCardProps, Space } from "antd";
import { createStyles } from "antd-style";

import { Typography } from "./typography/Typography";

import type { AvE2EProps } from "../types";

type StyledCardProps = AvE2EProps &
  AntdCardProps & {
    actionSlot?: ReactNode;
    icon?: ReactNode;
    children?: ReactNode;
    style?: CSSProperties;
    isSeparatorHidden?: boolean;
    isHeaderSeperatorHidden?: boolean;
    description?: ReactNode;
    /**
     * @deprecated use testIdPrefix instead
     */
    "data-testid"?: string;
    titleLevel?: 1 | 2 | 3 | 4 | 5;
  };

const useStyles = createStyles(({ token, css }, props: { isSeparatorHidden?: boolean }) => ({
  card: css`
    border-radius: ${token.borderRadiusLG}px;
    box-shadow: ${token.boxShadowTertiary};
    padding: ${token.paddingSM}px ${token.paddingXS}px 0px ${token.paddingXS}px;
    margin-bottom: ${token.marginLG}px;
    min-width: 200px;
    margin-top: ${!props.isSeparatorHidden ? token.paddingLG + "px" : "0px"};
  `,
  isHeaderSeperatorHidden: css`
    &.ant-card > .ant-card-head {
      border-bottom: none;
    }
  `,
  alignIcon: css`
    margin-bottom: 10px !important;
  `,
}));

function CustomCard(props: StyledCardProps) {
  const {
    actionSlot,
    icon,
    isSeparatorHidden = true,
    children,
    style,
    description,
    title,
    titleLevel,
    testIdPrefix,
    rootClassName,
    isHeaderSeperatorHidden = true,
    ...rest
  } = props;
  const { styles, cx } = useStyles({ isSeparatorHidden });

  return (
    <AntdCard
      data-testid={props.testIdPrefix ? `${props.testIdPrefix}-card` : props["data-testid"]}
      style={style}
      rootClassName={cx(
        styles.card,
        isHeaderSeperatorHidden ? styles.isHeaderSeperatorHidden : "",
        rootClassName
      )}
      title={
        title && (
          <Space>
            {icon}
            <Typography.Title
              level={titleLevel ?? 4}
              alignWithIcon={!!icon}
              testIdPrefix={testIdPrefix}
            >
              {title}
            </Typography.Title>
          </Space>
        )
      }
      {...rest}
      extra={actionSlot}
    >
      {children}
      {description && <Card.Meta description={description} />}
    </AntdCard>
  );
}

export const Card = Object.assign(CustomCard, {
  Meta: AntdCard.Meta,
  Grid: AntdCard.Grid,
});
